// TODO: The whole file content is copied from dashboard.
// Once mono-repo is ready, we should use the shared file.
import { createGlobalState } from "@vueuse/core";
import { defineAsyncComponent, markRaw, ref } from "vue";

function generateRandomId() {
  return Math.random().toString(36).substring(2, 9);
}

// Note: In comparison to dashboard we have removed the request method and loader toast types.
export type ToastType = "success" | "warning" | "error";

export interface ToastAction {
  label: string;
  handler: () => Promise<void> | void;
}

export type ToastMessageId = ReturnType<typeof generateRandomId>;

export interface ToastMessageData {
  title: string;
  subTitle?: string;
  type: ToastType;
  action?: ToastAction;
  canClose?: boolean;
  autoClose?: boolean;
  duration?: number;
}

export const TOAST_DEFAULT_DURATION = 5_000 as const;

export const TOAST_ICONS: Record<ToastType, ReturnType<typeof defineAsyncComponent>> = {
  // Note: In comparison to dashboard, we are using the actual icon components.
  success: markRaw(defineAsyncComponent(() => import("@/components/icons/status/IconSuccess.vue"))),
  warning: markRaw(defineAsyncComponent(() => import("@/components/icons/status/IconWarning.vue"))),
  error: markRaw(defineAsyncComponent(() => import("@/components/icons/status/IconError.vue"))),
};

export class ToastMessage {
  toastInstance: Toast;
  id: ToastMessageId;
  title: string;
  subTitle?: string;
  type: ToastType;
  canClose: boolean;
  autoClose: boolean;
  duration: number;
  action?: ToastAction;
  icon: (typeof TOAST_ICONS)[keyof typeof TOAST_ICONS];

  constructor(data: ToastMessageData & { toastInstance: Toast }) {
    this.id = generateRandomId();
    this.toastInstance = data.toastInstance;
    this.title = data.title;
    this.subTitle = data.subTitle || "";
    this.type = data.type;
    this.canClose = data.canClose ?? true;
    this.autoClose = data.autoClose ?? true;
    this.duration = data.duration || TOAST_DEFAULT_DURATION;
    this.icon = TOAST_ICONS[data.type];

    if (data.action) {
      const { handler: actionHandler, label } = data.action;
      this.action = {
        label,
        handler: () => {
          actionHandler();
          this.destroy();
        },
      };
    }

    this.postInit();
  }

  private postInit() {
    if (this.canClose && this.autoClose) {
      setTimeout(() => this.destroy(), this.duration);
    }
  }

  destroy() {
    return this.toastInstance.removeMessage(this.id);
  }
}

export class Toast {
  messages: Array<ToastMessage> = [];

  addMessage(data: ToastMessageData) {
    if (this.messages.some((msg) => msg.title === data.title)) {
      return undefined;
    }

    const newMessage = new ToastMessage({ ...data, toastInstance: this });

    this.messages.push(newMessage);
    return newMessage;
  }

  removeMessage(id: ToastMessageId) {
    this.messages = this.messages.filter((msg) => msg.id !== id);
  }

  removeAllMessages() {
    this.messages = [];
  }

  removeAllMessagesExcept(type: ToastType) {
    this.messages = this.messages.filter((message) => message.type === type);
  }
}

const useToastState = createGlobalState(() => ref(new Toast()));

export default useToastState;
